import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  getCurrentUser,
  getPreferences as getPreferencesRequest,
  login as loginRequest,
  logout as logoutRequest,
  SESSION_EXPIRED_EVENT,
} from '../api';

import i18n, { isSupportedLanguage, persistLanguage } from '../i18n';

import AuthContext from './AuthContext';

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const response = await getCurrentUser();

      setUser(response.user ?? null);

      return response.user ?? null;
    } catch (error) {
      if (error.status === 401) {
        setUser(null);

        return null;
      }

      throw error;
    }
  }, []);

  const refreshPreferences = useCallback(async () => {
    try {
      const response = await getPreferencesRequest();

      setPreferences(response.preferences ?? null);

      return response.preferences ?? null;
    } catch (error) {
      if (error.status === 401) {
        setPreferences(null);

        return null;
      }

      throw error;
    }
  }, []);

  const login = useCallback(
    async ({
      login,
      password,
      rememberMe = false,
      otp,
      rememberDevice = false,
    }) => {
      const response = await loginRequest({
        login,
        password,
        rememberMe,
        otp,
        rememberDevice,
      });

      const authenticatedUser =
        response.user ?? null;

      setUser(authenticatedUser);

      try {
        const preferencesResponse =
          await getPreferencesRequest();

        setPreferences(
          preferencesResponse.preferences ?? null,
        );
      } catch (error) {
        console.error(
          'Could not load interface preferences:',
          error,
        );
      }

      return authenticatedUser;
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await logoutRequest();
    } finally {
      setUser(null);
      setPreferences(null);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function restoreSession() {
      try {
        const response = await getCurrentUser();

        if (!isMounted) {
          return;
        }

        setUser(response.user ?? null);

        try {
          const preferencesResponse =
            await getPreferencesRequest();

          if (isMounted) {
            setPreferences(
              preferencesResponse.preferences ??
                null,
            );
          }
        } catch (preferencesError) {
          console.error(
            'Could not load interface preferences:',
            preferencesError,
          );
        }
      } catch (error) {
        if (!isMounted) {
          return;
        }

        if (error.status === 401) {
          setUser(null);
          return;
        }

        console.error(
          'Could not restore authentication session:',
          error,
        );

        setUser(null);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    restoreSession();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    function handleSessionExpired(event) {
      setUser(null);
      setPreferences(null);

      // 'Authentication required' means there was no session cookie at
      // all (e.g. a first, never-logged-in visit) - not worth an
      // explanatory banner. The other two messages mean a real session
      // existed and died (expired/revoked/inactivity timeout), including
      // on a fresh page load after the tab was closed for hours, which is
      // why this can't be based on this component's own (reset-on-reload)
      // `user` state - the server's answer is the only reliable signal.
      if (event.detail?.message !== 'Authentication required') {
        try {
          sessionStorage.setItem(
            'disiSessionExpired',
            '1',
          );
        } catch {
          // Storage unavailable - user is still logged out either way,
          // they just won't see the explanatory banner on /login.
        }
      }
    }

    window.addEventListener(
      SESSION_EXPIRED_EVENT,
      handleSessionExpired,
    );

    return () => {
      window.removeEventListener(
        SESSION_EXPIRED_EVENT,
        handleSessionExpired,
      );
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle(
      'no-animations',
      preferences?.animationsEnabled === false,
    );
  }, [preferences]);

  useEffect(() => {
    const theme = preferences?.theme;
    const root = document.documentElement;

    // Anything else (including no preference yet, or a legacy 'dark'/'system'
    // value from before those options were removed) leaves data-theme unset,
    // which is the platform's own look - the MV design.
    if (theme === 'high-contrast' || theme === 'classic') {
      root.setAttribute('data-theme', theme);
    } else {
      root.removeAttribute('data-theme');
    }
  }, [preferences]);

  useEffect(() => {
    const fontSize = preferences?.fontSize;
    const root = document.documentElement;

    if (fontSize === 'large' || fontSize === 'x-large') {
      root.setAttribute('data-font-size', fontSize);
    } else {
      root.removeAttribute('data-font-size');
    }
  }, [preferences]);

  useEffect(() => {
    // Anything but German or English (a value saved by an older version)
    // is ignored and the platform stays on its default.
    if (!isSupportedLanguage(preferences?.language)) {
      return;
    }

    if (i18n.language !== preferences.language) {
      i18n.changeLanguage(preferences.language);
    }

    persistLanguage(preferences.language);
  }, [preferences]);

  const value = useMemo(
    () => ({
      user,
      preferences,
      isLoading,
      isAuthenticated: Boolean(user),
      login,
      logout,
      refreshUser,
      refreshPreferences,
    }),
    [
      user,
      preferences,
      isLoading,
      login,
      logout,
      refreshUser,
      refreshPreferences,
    ],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthProvider;
